// Type aliases, hopefully to make code more readable
type TreeChildren<Node> = (Node) => Node[];
type TreeNodeMapper<Node, T> = (Node) => T;
type Path<T> = T[];
type Paths<T> = Path<T>[];

const IDENTITY: TreeNodeMapper<any, any> = (x: any): any => x;

export class TreeUtilsClass {
  /**
   * Extracts all path readouts from the root to each leaf of a tree, including
   * readouts to intermediate nodes.
   *
   * @param {Node} node - the root node of the tree
   * @param {(Node) => Node[]} getChildren - function to get child nodes for a given node
   * @param {(Node) => any} mapNode - transform function to apply to each node
   * @param {any[]} pathPrefix - readout to this node
   * @param {any[][]} allPaths - accumulator for all paths
   * @returns {any[][]} - array of all path readouts, each being an array of path elements
   **/
  pathReadout<Node, T>(
    node: Node,
    getChildren: TreeChildren<Node> = () => [],
    mapNode: TreeNodeMapper<Node, T> = IDENTITY,
    pathPrefix: Path<T> = [],
    allPaths: Paths<T> = [],
  ): Paths<T> {
    if (!node) {
      return allPaths;
    }

    // extend the path with current node
    const nodeEntry = mapNode(node);
    const path = pathPrefix.concat([nodeEntry]);
    const allPathsExtended = allPaths.concat([path]);

    // process children
    const children = getChildren(node);

    // terminate recursion
    if (!Array.isArray(children)) {
      return allPathsExtended;
    }

    // recurse
    const walkChildren = (pathsSoFar, child) =>
      this.pathReadout(child, getChildren, mapNode, path, pathsSoFar);
    return children.reduce(walkChildren, allPathsExtended);
  }

  /**
   * Collects the nodes within a tree into an array
   *
   * @param {Node} node - the root node of a tree
   * @param {(Node) => Node[]} getChildren - function to get child nodes for a given node
   * @param {Node[]} nodes - accumulator of all traversed nodes
   * @returns {Node[]} all nodes within a tree
   **/
  collectNodes<Node>(
    node: Node,
    getChildren: (Node) => Node[] = () => [],
    nodes: Node[] = [],
  ): Node[] {
    if (!node) {
      return nodes;
    }

    const nodesExtended = nodes.concat([node]);

    const children = getChildren(node);

    // terminate recursion
    if (!Array.isArray(children)) {
      return nodesExtended;
    }

    // recurse
    const walkChildren = (nodesSoFar, child) =>
      this.collectNodes(child, getChildren, nodesSoFar);
    return children.reduce(walkChildren, nodesExtended);
  }
}

export const TreeUtils = new TreeUtilsClass();
